"use client";

import { c as _c } from "react/compiler-runtime";
import { applyTrailingSlashPreference } from "@multi-tenant-cms/seo-runtime/deployment-url";
import { useDeferredValue, useMemo, useState } from "react";
import { InterlinkPreviewRoot } from "@/components/interlink-hover-preview";
import { GLOSSARY_INDEX_LETTERS, type GlossaryIndexLetter, groupGlossaryItemsByLetter } from "@/lib/glossary-letter-groups";
import type { PublicGlossaryCategory, PublicGlossaryItem, PublicGlossarySettings } from "@/lib/load-public-glossary";
import { deploymentBasePath } from "@/lib/public-deployment-url";
const LOCALE_COLLATOR = new Intl.Collator(undefined, {
  sensitivity: "base"
});
function glossaryNavHref(basePath: string, categorySlug: string | null, trailingSlash: boolean): string {
  const path = categorySlug === null ? basePath : `${basePath}/${encodeURIComponent(categorySlug)}`;
  return applyTrailingSlashPreference(path, trailingSlash);
}
function GlossaryCategoryNav(t0) {
  const $ = _c(18);
  const {
    basePath,
    categories,
    activeCategorySlug,
    trailingSlash
  } = t0;
  const t1 = activeCategorySlug === null ? "page" : undefined;
  let t2;
  if ($[0] !== basePath || $[1] !== trailingSlash) {
    t2 = glossaryNavHref(basePath, null, trailingSlash);
    $[0] = basePath;
    $[1] = trailingSlash;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <a aria-current={t1} className="rounded-full border border-border px-3 py-1.5 font-medium text-sm hover:bg-muted aria-[current=page]:border-primary aria-[current=page]:bg-primary aria-[current=page]:text-primary-foreground" href={t2}>All / uncategorized</a>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== activeCategorySlug || $[7] !== basePath || $[8] !== categories || $[9] !== trailingSlash) {
    let t5;
    if ($[11] !== activeCategorySlug || $[12] !== basePath || $[13] !== trailingSlash) {
      t5 = category => <a aria-current={activeCategorySlug === category.slug ? "page" : undefined} className="rounded-full border border-border px-3 py-1.5 text-sm hover:bg-muted aria-[current=page]:border-primary aria-[current=page]:bg-primary aria-[current=page]:text-primary-foreground" href={glossaryNavHref(basePath, category.slug, trailingSlash)} key={category.slug}>{category.label}</a>;
      $[11] = activeCategorySlug;
      $[12] = basePath;
      $[13] = trailingSlash;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t4 = categories.map(t5);
    $[6] = activeCategorySlug;
    $[7] = basePath;
    $[8] = categories;
    $[9] = trailingSlash;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[15] !== t3 || $[16] !== t4) {
    t5 = <nav aria-label="Glossary categories" className="mb-6 flex flex-wrap gap-2">{t3}{t4}</nav>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
function GlossaryLetterNav(t0) {
  const $ = _c(4);
  const {
    activeLetters
  } = t0;
  let t1;
  if ($[0] !== activeLetters) {
    t1 = GLOSSARY_INDEX_LETTERS.map(letter => activeLetters.has(letter) ? <a className="inline-flex h-8 min-w-8 items-center justify-center rounded-md font-medium text-primary text-sm hover:bg-muted" href={`#glossary-letter-${letter}`} key={letter}>{letter}</a> : <span aria-hidden={true} className="inline-flex h-8 min-w-8 items-center justify-center rounded-md text-muted-foreground/35 text-sm" key={letter}>{letter}</span>);
    $[0] = activeLetters;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <nav aria-label="Jump to letter" className="sticky top-0 z-10 -mx-4 mb-8 flex flex-wrap justify-center gap-1 border-border border-b bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">{t1}</nav>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function GlossaryItemLink(t0) {
  const $ = _c(12);
  const {
    item,
    linkPreviewEnabled
  } = t0;
  let t1;
  if ($[0] !== item.entryId || $[1] !== item.excerpt || $[2] !== item.href || $[3] !== item.id || $[4] !== item.label || $[5] !== item.source || $[6] !== linkPreviewEnabled) {
    t1 = linkPreviewEnabled && item.entryId ? {
      className: "interlink text-foreground underline decoration-primary/40 underline-offset-2 hover:decoration-primary",
      "data-interlink-entry-id": item.entryId
    } : linkPreviewEnabled && item.source === "manual" ? {
      className: "interlink text-foreground underline decoration-primary/40 underline-offset-2 hover:decoration-primary",
      "data-interlink-glossary-term-id": item.id,
      "data-interlink-title": item.label,
      "data-interlink-excerpt": item.excerpt,
      "data-interlink-path": item.href
    } : {
      className: "text-foreground underline decoration-primary/40 underline-offset-2 hover:decoration-primary"
    };
    $[0] = item.entryId;
    $[1] = item.excerpt;
    $[2] = item.href;
    $[3] = item.id;
    $[4] = item.label;
    $[5] = item.source;
    $[6] = linkPreviewEnabled;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== item.href || $[9] !== item.label || $[10] !== t1) {
    t2 = <a {...t1} href={item.href}>{item.label}</a>;
    $[8] = item.href;
    $[9] = item.label;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
function GlossaryLetterSection(t0) {
  const $ = _c(15);
  const {
    letter,
    items,
    linkPreviewEnabled
  } = t0;
  if (items.length === 0) {
    return null;
  }
  const t1 = `glossary-letter-heading-${letter}`;
  const t2 = `glossary-letter-${letter}`;
  const t3 = `glossary-letter-heading-${letter}`;
  let t4;
  if ($[0] !== letter || $[1] !== t3) {
    t4 = <h2 className="mb-4 border-border border-b pb-2 font-semibold text-2xl text-primary tracking-wide" id={t3}>{letter}</h2>;
    $[0] = letter;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== items || $[4] !== linkPreviewEnabled) {
    let t6;
    if ($[6] !== linkPreviewEnabled) {
      t6 = item => <li className="border-border/60 border-b pb-5 last:border-b-0" id={item.source === "manual" ? `glossary-term-${item.id}` : undefined} key={`${item.source}-${item.id}`}><h3 className="font-medium text-base leading-snug"><GlossaryItemLink item={item} linkPreviewEnabled={linkPreviewEnabled} /><span className="ml-2 font-normal text-muted-foreground text-xs">{item.source === "manual" ? "Glossary term" : "Entry"}</span></h3>{item.excerpt ? <p className="mt-1.5 whitespace-pre-line text-muted-foreground text-sm leading-relaxed">{item.excerpt}</p> : null}</li>;
      $[6] = linkPreviewEnabled;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    t5 = items.map(t6);
    $[3] = items;
    $[4] = linkPreviewEnabled;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <ul className="m-0 flex list-none flex-col gap-5 p-0">{t5}</ul>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t4 || $[13] !== t6) {
    t7 = <section aria-labelledby={t1} className="scroll-mt-24" id={t2}>{t4}{t6}</section>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function GlossaryItemList(t0) {
  const $ = _c(3);
  const {
    children,
    linkPreviewEnabled
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== linkPreviewEnabled) {
    t1 = linkPreviewEnabled ? <InterlinkPreviewRoot apiBasePath={deploymentBasePath()}>{children}</InterlinkPreviewRoot> : children;
    $[0] = children;
    $[1] = linkPreviewEnabled;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function PublicGlossaryIndex(t0) {
  const $ = _c(52);
  const {
    basePath,
    categorySlug,
    items,
    rootHeading: t1,
    settings,
    trailingSlash: t2
  } = t0;
  const rootHeading = t1 === undefined ? "Glossary" : t1;
  const trailingSlash = t2 === undefined ? false : t2;
  const [query, setQuery] = useState("");
  let t3;
  if ($[0] !== query) {
    t3 = query.trim().toLocaleLowerCase();
    $[0] = query;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const deferredQuery = useDeferredValue(t3);
  let t4;
  if ($[2] !== categorySlug || $[3] !== items) {
    let t5;
    if ($[5] !== categorySlug) {
      t5 = item => item.categorySlug === categorySlug;
      $[5] = categorySlug;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    t4 = items.filter(t5).toSorted(_temp);
    $[2] = categorySlug;
    $[3] = items;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const scopedItems = t4;
  let t5;
  bb0: {
    if (!deferredQuery) {
      t5 = scopedItems;
      break bb0;
    }
    let t6;
    if ($[7] !== deferredQuery || $[8] !== scopedItems) {
      let t7;
      if ($[10] !== deferredQuery) {
        t7 = item_0 => `${item_0.label} ${item_0.excerpt}`.toLocaleLowerCase().includes(deferredQuery);
        $[10] = deferredQuery;
        $[11] = t7;
      } else {
        t7 = $[11];
      }
      t6 = scopedItems.filter(t7);
      $[7] = deferredQuery;
      $[8] = scopedItems;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t5 = t6;
  }
  const filteredItems = t5;
  let t6;
  if ($[12] !== filteredItems) {
    t6 = groupGlossaryItemsByLetter(filteredItems);
    $[12] = filteredItems;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const groupedItems = t6;
  let t7;
  if ($[14] !== groupedItems) {
    t7 = new Set(GLOSSARY_INDEX_LETTERS.filter(letter => (groupedItems.get(letter)?.length ?? 0) > 0));
    $[14] = groupedItems;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const activeLetters = t7;
  let t8;
  if ($[16] !== categorySlug || $[17] !== settings.categories) {
    let t9;
    if ($[19] !== categorySlug) {
      t9 = category => category.slug === categorySlug;
      $[19] = categorySlug;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    t8 = settings.categories.find(t9);
    $[16] = categorySlug;
    $[17] = settings.categories;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const activeCategory = t8;
  const heading = activeCategory ? `${activeCategory.label} glossary` : rootHeading;
  const emptyCopy = settings.emptyStateCopy ?? "No glossary terms are available for this section yet.";
  let t9;
  if ($[21] !== heading) {
    t9 = <h1 className="font-semibold text-3xl tracking-tight" id="glossary-page-heading">{heading}</h1>;
    $[21] = heading;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <p className="mt-2 text-muted-foreground text-sm leading-relaxed">Browse the site's published reference terms and opted-in content.</p>;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = scopedItems.length === 1 ? "entry" : "entries";
  let t12;
  if ($[24] !== scopedItems.length || $[25] !== t11) {
    t12 = <p className="mt-1 text-muted-foreground text-xs">{scopedItems.length} {t11}{" "}in this view.</p>;
    $[24] = scopedItems.length;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t9) {
    t13 = <header className="mb-6 border-border border-b pb-6">{t9}{t10}{t12}</header>;
    $[27] = t12;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== basePath || $[31] !== categorySlug || $[32] !== settings.categories || $[33] !== settings.enableCategories || $[34] !== trailingSlash) {
    t14 = settings.enableCategories ? <GlossaryCategoryNav activeCategorySlug={categorySlug} basePath={basePath} categories={settings.categories} trailingSlash={trailingSlash} /> : null;
    $[30] = basePath;
    $[31] = categorySlug;
    $[32] = settings.categories;
    $[33] = settings.enableCategories;
    $[34] = trailingSlash;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== query || $[37] !== settings.enableSearch) {
    t15 = settings.enableSearch ? <div className="mb-6"><label className="sr-only" htmlFor="glossary-search">Search glossary</label><input className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring" id="glossary-search" onChange={event => setQuery(event.target.value)} placeholder="Search glossary terms" type="search" value={query} /></div> : null;
    $[36] = query;
    $[37] = settings.enableSearch;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== activeLetters || $[40] !== deferredQuery || $[41] !== emptyCopy || $[42] !== filteredItems.length || $[43] !== groupedItems || $[44] !== query || $[45] !== settings.enableLinkPreview) {
    t16 = filteredItems.length === 0 ? <p aria-live="polite" className="text-muted-foreground text-sm">{deferredQuery ? `No glossary terms match “${query.trim()}”.` : emptyCopy}</p> : <GlossaryItemList linkPreviewEnabled={settings.enableLinkPreview}><GlossaryLetterNav activeLetters={activeLetters} /><div className="flex flex-col gap-12">{GLOSSARY_INDEX_LETTERS.map(letter_0 => <GlossaryLetterSection items={groupedItems.get(letter_0) ?? []} key={letter_0} letter={letter_0} linkPreviewEnabled={settings.enableLinkPreview} />)}</div></GlossaryItemList>;
    $[39] = activeLetters;
    $[40] = deferredQuery;
    $[41] = emptyCopy;
    $[42] = filteredItems.length;
    $[43] = groupedItems;
    $[44] = query;
    $[45] = settings.enableLinkPreview;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t13 || $[48] !== t14 || $[49] !== t15 || $[50] !== t16) {
    t17 = <article aria-labelledby="glossary-page-heading" className="px-4 py-10">{t13}{t14}{t15}{t16}</article>;
    $[47] = t13;
    $[48] = t14;
    $[49] = t15;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  return t17;
}
function _temp(a, b) {
  return LOCALE_COLLATOR.compare(a.label, b.label);
}